import { useEffect } from 'react';

import cx from 'classnames';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useNetworkState } from 'react-use';

import bullsImg from 'assets/bg/bg.jpeg';
import Button from 'components/shared/Button';
import PageHeaderMenu from 'components/shared/LandingPage/blocks/PageHeader/PageHeaderMenu';
import Loader from 'components/shared/Loader';
import MobileAppInfo from 'components/shared/MobileAppInfo';
import { useCustomFonts, useCustomStyles } from 'hooks/useCustomFront';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useGTM } from 'hooks/useGTM';
import i18n from 'i18n';
import { logoutThunk } from 'store/auth/thunks';
import { getLandingTheme, LANDING_THEMES } from 'utils/itineraries/functions';

import { EDetailsMode } from '../../Admin/Itinerary/Details/types';
import { useGetPrimaryImageUrl } from '../functions';
import NotFoundWrapper from '../NotFoundWrapper';
import { GP_AUTH_SIGNIN_ROUTE } from '../Routes/constants';
import { useGuestPortalRouteBuilder } from '../Routes/functions';
import styles from './index.module.css';
import { useDashboardLoader } from './useDashboardLoader';

function DashboardContent({ landingTheme, guestPortal, itinerary }) {
  useDocumentTitle({ title: guestPortal.title });

  const { getGuestPortalPath, getItineraryPath } = useGuestPortalRouteBuilder();
  const dispatch = useDispatch();
  const { online } = useNetworkState();

  const css =
    guestPortal.detailsMode === EDetailsMode.Original
      ? guestPortal.itinerary.customStyles
      : guestPortal.landingConfig?.css;
  useCustomStyles({ css, customId: guestPortal.itinerary.id });
  useCustomFonts({ landingItem: guestPortal.itinerary, customId: guestPortal.itinerary.id });

  const { primaryUrl } = useGetPrimaryImageUrl(itinerary);

  return (
    <div
      className={cx(
        styles.container,
        'custom-gp-dashboard-container',
        landingTheme !== LANDING_THEMES.default && styles.containerNomad,
        landingTheme === LANDING_THEMES.jbd && styles.containerJbd,
      )}
    >
      {/*
      TODO: attribute `crossOrigin` was removed because it broke request to image in some cases after release "Tasks Manager".
      But we need to think how to return this attribute back.
      */}
      <img className={styles.bg} src={primaryUrl || bullsImg} alt="Background Image" />

      <div className={cx(styles.header, 'custom-gp-dashboard-header')}>
        <div className={cx(styles.headerUserMenu, 'custom-gp-dashboard-header-user-menu')}>
          <PageHeaderMenu
            theme={landingTheme}
            userMenuLabelClassName={cx(styles.userMenuLabel)}
            linkClassName={landingTheme !== LANDING_THEMES.default && styles.linkClassNameNomad}
            actions={
              online
                ? [
                    {
                      label: i18n.t('proposal_content.log_out'),
                      onClick: () => dispatch(logoutThunk({ getSignInPath: () => GP_AUTH_SIGNIN_ROUTE })),
                    },
                  ]
                : []
            }
            alwaysShowDesktop
          />
        </div>
      </div>

      <div className={cx(styles.content, 'custom-gp-dashboard-content')}>
        {itinerary.preparedFor && (
          <div className={cx(styles.preparedFor, 'custom-gp-dashboard-prepared-for')}>
            {i18n.t('proposal_content.prepared_for')} {itinerary.preparedFor}
          </div>
        )}
        {guestPortal.title && <div className={cx(styles.title, 'custom-gp-dashboard-title')}>{guestPortal.title}</div>}
        {itinerary.proposedDates && (
          <div className={cx(styles.proposedDates, 'custom-gp-dashboard-travel-dates')}>{itinerary.proposedDates}</div>
        )}

        <div className={cx(styles.cards, 'custom-gp-dashboard-cards')}>
          <div className={cx(styles.card, 'custom-gp-dashboard-card')}>
            <div className={cx(styles.cardTitle, 'custom-gp-dashboard-card-title')}>
              {i18n.t('proposal_content.guest_portal.splash.itinerary.title')}
            </div>
            <div className={cx(styles.cardDescr, 'custom-gp-dashboard-card-descr')}>
              {landingTheme === LANDING_THEMES.jbd
                ? 'Your Detailed Itinerary'
                : i18n.t('proposal_content.guest_portal.splash.itinerary.description')}
            </div>
            <Button
              type="black"
              className={cx(styles.cardBtn, 'custom-gp-dashboard-card-btn')}
              to={getItineraryPath(itinerary.id)}
              reactRouterLink
            >
              {i18n.t('proposal_content.guest_portal.splash.enter')}
            </Button>
          </div>

          <div className={cx(styles.card, 'custom-gp-dashboard-card')}>
            <div className={cx(styles.cardTitle, 'custom-gp-dashboard-card-title')}>
              {i18n.t('proposal_content.guest_portal.splash.info.title')}
            </div>
            <div className={cx(styles.cardDescr, 'custom-gp-dashboard-card-descr')}>
              {landingTheme === LANDING_THEMES.jbd
                ? 'Your Guest Portal'
                : i18n.t('proposal_content.guest_portal.splash.info.description')}
            </div>
            <Button
              type="black"
              className={cx(styles.cardBtn, 'custom-gp-dashboard-card-btn')}
              to={getGuestPortalPath(guestPortal.id)}
              reactRouterLink
            >
              {i18n.t('proposal_content.guest_portal.splash.enter')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const { data: guestPortal, error, isLoading, refetch } = useDashboardLoader();
  const { guestPortalId: accessToken } = useParams();

  useGTM(guestPortal?.account?.gtmContainerId);

  const itinerary = guestPortal?.itinerary || {};
  const landingTheme = getLandingTheme(itinerary);

  useEffect(() => {
    if (isLoading || landingTheme === LANDING_THEMES.default) {
      return;
    }

    const themeToApply = LANDING_THEMES[landingTheme];

    if (themeToApply) {
      document.body.classList.add(`landing-theme-${themeToApply}`, 'landing-guest-domain');
    }

    return () => {
      if (themeToApply) {
        document.body.classList.remove(`landing-theme-${themeToApply}`, 'landing-guest-domain');
      }
    };
  }, [isLoading, landingTheme]);

  if (error) {
    return <NotFoundWrapper onRetry={refetch} />;
  }

  if (isLoading) {
    return <Loader isFillBlock />;
  }

  return (
    <>
      <DashboardContent guestPortal={guestPortal} itinerary={itinerary} landingTheme={landingTheme} />
      <MobileAppInfo proposal={guestPortal} accessToken={accessToken} />
    </>
  );
}

export default Dashboard;
